import BrButton from '@govbr/react-components/lib/BrButton';
import React, { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { format, parseISO } from 'date-fns';
import {
  registrarAcaoIndicativoNascimento,
  salvarIntegranteIndicativoNascimento,
  selectFamiliaIndicativoNascimento,
  selectIntegranteIndicativoNascimento,
  selectRespostasIndicativo,
  setEtapaIndicativo,
  setEtapaPerguntaAtual,
} from '../indicativoNascimentoSlice';
import { AcaoIndicativoNascimento } from '../../../api/indicativoNascimentoAPI';
import { selectNumeroFamiliar, selectPerfilCompleto } from '../../Perfil/perfilSlice';
import { PERGUNTAS } from '../IndicativoNascimentoPerguntas/perguntas';
import ModalNaoIncluir from '../ModalNaoIncluir/ModalNaoIncluir';
import styles from './IndicativoNascimentoResumo.module.css';

const LABEL_OPCAO: Record<string, Record<string, string>> = {
  deficiencia: { sim: 'Sim', nao: 'Não possui deficiência permanente' },
  sexo: { feminino: 'Feminino', masculino: 'Masculino', F: 'Feminino', M: 'Masculino' },
  parentesco: {
    filho: 'Filho(a)',
    enteado: 'Enteado(a)',
    neto_bisneto: 'Neto(a) ou bisneto(a)',
    irmao: 'Irmão ou irmã',
    outro_parente: 'Outro parente',
    nao_parente: 'Não parente',
  },
  cor_raca: { branca: 'Branca', preta: 'Preta', amarela: 'Amarela', parda: 'Parda', indigena: 'Indígena' },
};

const IndicativoNascimentoResumo: React.FC = () => {
  const dispatch = useAppDispatch();
  const respostas = useAppSelector(selectRespostasIndicativo);
  const integrante = useAppSelector(selectIntegranteIndicativoNascimento);
  const familia = useAppSelector(selectFamiliaIndicativoNascimento);
  const perfil = useAppSelector(selectPerfilCompleto);
  const numeroFamiliar = useAppSelector(selectNumeroFamiliar);
  const [declaracao, setDeclaracao] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [loadingNaoIncluir, setLoadingNaoIncluir] = useState(false);
  const [loadingConcluir, setLoadingConcluir] = useState(false);

  const nomeCrianca = (integrante?.nome?.split(' ')?.[0] ?? '').toUpperCase();

  const handleCancelarNoResumo = async () => {
    if (familia?.nuFamiliar && integrante?.cpf) {
      setLoadingNaoIncluir(true);
      await dispatch(registrarAcaoIndicativoNascimento({
        numeroFamiliar: familia.nuFamiliar,
        cpfIntegrante: integrante.cpf,
        codigoAcao: AcaoIndicativoNascimento.NAO_DESEJA_INCLUIR,
      }));
      setLoadingNaoIncluir(false);
    }
    setModalAberto(false);
    dispatch(setEtapaIndicativo('compareca-ao-cras'));
  };

  const concluir = () => {
    if (!integrante || !numeroFamiliar) return;
    setLoadingConcluir(true);
    dispatch(salvarIntegranteIndicativoNascimento({ numeroFamiliar: String(numeroFamiliar), integrante, respostas }))
      .unwrap()
      .then(() => dispatch(setEtapaIndicativo('conclusao')))
      .catch((err: any) => {
        const codigo = err?.codigo;
        const message = err?.message ?? '';
        if (codigo === '39') {
          dispatch(setEtapaIndicativo('erro-horario'));
        } else if (message.includes('parceiro indispon')) {
          dispatch(setEtapaIndicativo('erro-servico'));
        } else {
          dispatch(setEtapaIndicativo('erro'));
        }
      })
      .finally(() => setLoadingConcluir(false));
  };

  const perguntasVisiveis = useMemo(() => {
    return PERGUNTAS.filter((p) => {
      if (p.id === 'sexo') return !integrante?.sexo;
      return p.exibir !== false;
    });
  }, [integrante?.sexo]);

  const totalEtapas = perguntasVisiveis.length + 1;
  const etapaResumo = totalEtapas - 1;

  const nomeRF = (perfil?.nome?.split(' ')?.[0] ?? '').toUpperCase();

  const dataNascimento = integrante?.dataNascimento
    ? format(parseISO(integrante.dataNascimento), 'dd/MM/yyyy')
    : '-';

  const sexoLabel = integrante?.sexo
    ? (LABEL_OPCAO.sexo[integrante.sexo] ?? integrante.sexo)
    : (respostas['sexo'] ? LABEL_OPCAO.sexo[respostas['sexo']] : '-');

  const corrigirDados = () => {
    dispatch(setEtapaPerguntaAtual(0));
    dispatch(setEtapaIndicativo('perguntas'));
  };

  const getValor = (id: string): string => {
    const resposta = respostas[id];
    if (!resposta) return '-';
    return LABEL_OPCAO[id]?.[resposta] ?? resposta;
  };

  const getFiliacao = (): { filiacao1: string; filiacao2: string } => {
    const f1 = integrante?.filiacao1 ?? '';
    const resposta = respostas['filiacao'];
    if (!resposta) return { filiacao1: f1 || '-', filiacao2: '' };
    try {
      const parsed = JSON.parse(resposta);
      const filiacao1 = (parsed.filiacao1 && parsed.filiacao1 !== 'nao_sei') ? parsed.filiacao1 : (f1 || '-');
      const filiacao2 = (parsed.filiacao2 && parsed.filiacao2 !== 'nao_sei') ? parsed.filiacao2 : '';
      return { filiacao1, filiacao2 };
    } catch { return { filiacao1: f1 || resposta, filiacao2: '' }; }
  };

  const renderCampo = (id: string) => {
    const labels: Record<string, string> = {
      deficiencia: 'Deficiência',
      cor_raca: 'Cor/raça',
      parentesco: `Relação de parentesco com você (${nomeRF})`,
    };
    if (id === 'filiacao') {
      const { filiacao1, filiacao2 } = getFiliacao();
      return (
        <div key={id} className={styles.campo}>
          <span className={styles.campoLabel}>Filiação</span>
          <span className={styles.campoValor}>{filiacao1}</span>
          {filiacao2 && <span className={styles.campoValor}>{filiacao2}</span>}
        </div>
      );
    }
    return (
      <div key={id} className={styles.campo}>
        <span className={styles.campoLabel}>{labels[id] ?? id}</span>
        <span className={styles.campoValor}>{getValor(id)}</span>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.fechar} onClick={() => setModalAberto(true)}>✕</button>
        <span className={styles.headerTitulo}>Novo integrante na família</span>
      </div>

      <div className={styles.conteudo}>
        <div className={styles.progressoWrapper}>
          <div className={styles.progressoLinha} />
          {Array.from({ length: totalEtapas }).map((_, i) => (
            <React.Fragment key={i}>
              <div
                className={`${styles.progressoPonto} ${
                  i < etapaResumo ? styles.progressoPontoConcluido : styles.progressoPontoAtivo
                }`}
              />
              {i < totalEtapas - 1 && <div className={styles.progressoEspaco} />}
            </React.Fragment>
          ))}
        </div>

        <p className={styles.instrucao}>
          Antes de concluir, revise as informações sobre o novo integrante da sua família.
        </p>

        <div className={styles.campo}>
          <span className={styles.campoLabel}>Nome</span>
          <span className={styles.campoValor}>{integrante?.nome ?? '-'}</span>
        </div>

        <div className={styles.campoLinha}>
          <div className={styles.campo}>
            <span className={styles.campoLabel}>Nascimento</span>
            <span className={styles.campoValor}>{dataNascimento}</span>
          </div>
          <div className={styles.campo}>
            <span className={styles.campoLabel}>Sexo</span>
            <span className={styles.campoValor}>{sexoLabel}</span>
          </div>
        </div>

        {['parentesco', 'cor_raca', 'deficiencia', 'filiacao']
          .filter((id) => perguntasVisiveis.some((p) => p.id === id))
          .map((id) => renderCampo(id))}

        <label className={styles.declaracaoWrapper}>
          <input
            type="checkbox"
            checked={declaracao}
            onChange={(e) => setDeclaracao(e.target.checked)}
            className={styles.declaracaoCheckbox}
          />
          <span className={styles.declaracaoTexto}>
            Declaro, sob as penas da lei (Art. 299 do Código Penal), que as informações acima correspondem à verdade.
          </span>
        </label>

        <button className={styles.botaoCorrigir} onClick={corrigirDados}>
          Corrigir dados
        </button>
      </div>

      <div className={styles.footer}>
        <button className={styles.botaoAnterior} onClick={() => {
          dispatch(setEtapaPerguntaAtual(perguntasVisiveis.length - 1));
          dispatch(setEtapaIndicativo('perguntas'));
        }}>
          Anterior
        </button>
        <BrButton
          className={styles.botaoConcluir}
          onClick={concluir}
          disabled={!declaracao || loadingConcluir}
        >
          {loadingConcluir ? <div className={`loading small ${styles.spinnerBranco}`}></div> : 'Concluir'}
        </BrButton>
      </div>

      <ModalNaoIncluir
        open={modalAberto}
        variante="perguntas"
        nomeIntegrante={nomeCrianca}
        onContinuar={() => setModalAberto(false)}
        onNaoIncluir={handleCancelarNoResumo}
        loadingNaoIncluir={loadingNaoIncluir}
      />
    </div>
  );
};

export default IndicativoNascimentoResumo;
